import React, { createContext, useContext, useState, useMemo, useEffect, useCallback } from 'react';
import { todayStr } from '../api/client';
import { useAuth } from './AuthContext';
import db from '../db';

const AppContext = createContext(null);

const DEFAULT_GOALS = { calories: 2150, carbs: 269, fat: 72, protein: 108, water: 8 };

function loadGoals() {
  try {
    const raw = localStorage.getItem('trackr_goals');
    return raw ? { ...DEFAULT_GOALS, ...JSON.parse(raw) } : DEFAULT_GOALS;
  } catch {
    return DEFAULT_GOALS;
  }
}

function computeStreak(weightDates) {
  if (!weightDates.size) return 0;
  let streak = 0;
  const d = new Date();
  while (true) {
    const key = d.toISOString().slice(0, 10);
    if (!weightDates.has(key)) break;
    streak++;
    d.setDate(d.getDate() - 1);
  }
  return streak;
}

export const AppProvider = ({ children }) => {
  const { user: authUser, setUser: saveProfile } = useAuth();
  const [goals, setGoalsState] = useState(loadGoals);
  const [diary, setDiary] = useState({ Breakfast: [], Lunch: [], Dinner: [], Snacks: [] });
  const [exerciseLog, setExerciseLog] = useState([]);
  const [waterCups, setWaterCupsState] = useState(0);
  const [weights, setWeights] = useState([]);
  const [date] = useState(todayStr());
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!authUser) return;
    setLoading(true);
    try {
      const [diaryRows, exRows, waterRow, weightRows] = await Promise.all([
        db.diary.where('date').equals(date).toArray(),
        db.exercise.where('date').equals(date).toArray(),
        db.water.get(date),
        db.weight.orderBy('date').toArray(),
      ]);

      const norm = { Breakfast: [], Lunch: [], Dinner: [], Snacks: [] };
      for (const e of diaryRows) {
        const meal = e.meal || 'Snacks';
        if (!norm[meal]) norm[meal] = [];
        norm[meal].push({ ...e.food, qty: e.qty, logId: e.id });
      }
      setDiary(norm);
      setExerciseLog(exRows.map((e) => ({ ...e, id: e.id })));
      setWaterCupsState(waterRow?.cups || 0);
      setWeights(weightRows);
    } catch (e) {
      console.error('fetchAll failed', e);
    } finally {
      setLoading(false);
    }
  }, [authUser, date]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const totals = useMemo(() => {
    const meals = Object.values(diary).flat();
    const sum = (key) => meals.reduce((acc, m) => acc + (m[key] || 0) * (m.qty || 1), 0);
    return {
      calories: Math.round(sum('calories')),
      carbs: Math.round(sum('carbs')),
      fat: Math.round(sum('fat')),
      protein: Math.round(sum('protein')),
    };
  }, [diary]);

  const exerciseTotal = useMemo(
    () => exerciseLog.reduce((acc, e) => acc + (e.calories || 0), 0),
    [exerciseLog]
  );

  const addFood = async (meal, food) => {
    const entry = {
      date,
      meal,
      qty: 1,
      food: {
        name: food.name,
        brand: food.brand || 'Generic',
        serving: food.serving || '1 serving',
        calories: food.calories || 0,
        carbs: food.carbs || 0,
        fat: food.fat || 0,
        protein: food.protein || 0,
        sugar: food.sugar || 0,
        fiber: food.fiber || 0,
        sodium: food.sodium || 0,
        source: food.source,
        external_id: food.external_id,
      },
    };
    const id = await db.diary.add(entry);
    setDiary((prev) => ({
      ...prev,
      [meal]: [...prev[meal], { ...entry.food, qty: 1, logId: id }],
    }));
  };

  const removeFood = async (meal, logId) => {
    await db.diary.delete(logId);
    setDiary((prev) => ({ ...prev, [meal]: prev[meal].filter((f) => f.logId !== logId) }));
  };

  const addExercise = async (entry) => {
    const doc = { ...entry, date };
    const id = await db.exercise.add(doc);
    setExerciseLog((prev) => [...prev, { ...doc, id }]);
  };

  const removeExercise = async (id) => {
    await db.exercise.delete(id);
    setExerciseLog((prev) => prev.filter((e) => e.id !== id));
  };

  const logWeight = async (w) => {
    const weight = parseFloat(w);
    await db.weight.put({ date, weight });
    saveProfile({ ...authUser, currentWeight: weight });
    setWeights((prev) => {
      const filtered = prev.filter((p) => p.date !== date);
      return [...filtered, { date, weight }].sort((a, b) => a.date.localeCompare(b.date));
    });
  };

  const setWaterCups = async (cups) => {
    setWaterCupsState(cups);
    await db.water.put({ date, cups });
  };

  const setGoals = (updated) => {
    const merged = { ...goals, ...updated };
    localStorage.setItem('trackr_goals', JSON.stringify(merged));
    setGoalsState(merged);
  };

  const latestWeight = weights.length ? weights[weights.length - 1].weight : null;
  const weightDates = useMemo(() => new Set(weights.map((w) => w.date)), [weights]);
  const units = authUser?.units || 'imperial';
  const weightUnit = units === 'metric' ? 'kg' : 'lb';

  const user = useMemo(() => {
    if (!authUser) return null;
    return {
      name: authUser.name || 'You',
      username: '@' + (authUser.name || 'user').toLowerCase().replace(/\s+/g, ''),
      avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(authUser.name || 'U')}&background=0066ee&color=fff&size=200`,
      goal: authUser.goal || 'Lose Weight',
      units,
      weightUnit,
      startWeight: authUser.startWeight ?? (units === 'metric' ? 77 : 170),
      currentWeight: authUser.currentWeight ?? latestWeight ?? authUser.startWeight ?? (units === 'metric' ? 77 : 170),
      goalWeight: authUser.goalWeight ?? (units === 'metric' ? 68 : 150),
      height: authUser.height || (units === 'metric' ? '175 cm' : "5'9\""),
      age: authUser.age || 25,
      streak: computeStreak(weightDates),
      joinedDate: authUser.joinedDate || new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
    };
  }, [authUser, latestWeight, weightDates, units, weightUnit]);

  if (loading || !user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="w-10 h-10 border-4 border-[#0066ee] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <AppContext.Provider
      value={{
        user,
        goals,
        setGoals,
        diary,
        totals,
        exerciseLog,
        exerciseTotal,
        waterCups,
        setWaterCups,
        weights,
        loading,
        addFood,
        removeFood,
        addExercise,
        removeExercise,
        logWeight,
        refetch: fetchAll,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be inside AppProvider');
  return ctx;
};
